import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, Edit2, Trash2, Save, X, Loader2, 
  Briefcase, Eye, EyeOff, Shield, Users, ChevronRight
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { supabase } from '../../lib/supabase';

const AdminCareerJobs = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { language } = useLanguage();
  
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('jobs');
  const [formData, setFormData] = useState({
    slug: '',
    title_tr: '',
    title_en: '',
    department: '',
    location: '',
    employment_type: 'full-time',
    description_tr: '',
    description_en: '',
    requirements_tr: '',
    requirements_en: '',
    responsibilities_tr: '',
    responsibilities_en: '',
    is_active: true
  });

  const employmentTypes = [
    { value: 'full-time', labelTr: 'Tam Zamanlı', labelEn: 'Full-time' },
    { value: 'part-time', labelTr: 'Yarı Zamanlı', labelEn: 'Part-time' },
    { value: 'contract', labelTr: 'Sözleşmeli', labelEn: 'Contract' },
    { value: 'remote', labelTr: 'Uzaktan', labelEn: 'Remote' }
  ];

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/auth/login');
        return;
      }
      checkAdminRole();
    }
  }, [user, authLoading, navigate]);

  const checkAdminRole = async () => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .maybeSingle();

      const role = profile?.role || 'customer';
      setUserRole(role);

      if (role === 'admin' || role === 'editor') {
        loadJobs();
        loadApplications();
      }
    } catch (err) {
      console.error('Role check error:', err);
      setUserRole('customer');
    }
  };

  const loadJobs = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('career_jobs')
        .select('*')
        .order('created_at', { ascending: false });

      if (!error) setJobs(data || []);
    } catch (error) {
      console.error('Error loading jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadApplications = async () => {
    try {
      const { data, error } = await supabase
        .from('career_applications')
        .select('*, career_jobs(title_tr, title_en)')
        .order('created_at', { ascending: false });

      if (!error) setApplications(data || []);
    } catch (error) {
      console.error('Error loading applications:', error);
    }
  };

  const handleEdit = (job) => {
    setEditingId(job.id);
    setIsCreating(false);
    setFormData({
      slug: job.slug || '',
      title_tr: job.title_tr || '',
      title_en: job.title_en || '',
      department: job.department || '',
      location: job.location || '',
      employment_type: job.employment_type || 'full-time',
      description_tr: job.description_tr || '',
      description_en: job.description_en || '',
      requirements_tr: Array.isArray(job.requirements_tr) ? JSON.stringify(job.requirements_tr, null, 2) : (job.requirements_tr || '[]'),
      requirements_en: Array.isArray(job.requirements_en) ? JSON.stringify(job.requirements_en, null, 2) : (job.requirements_en || '[]'),
      responsibilities_tr: Array.isArray(job.responsibilities_tr) ? JSON.stringify(job.responsibilities_tr, null, 2) : (job.responsibilities_tr || '[]'),
      responsibilities_en: Array.isArray(job.responsibilities_en) ? JSON.stringify(job.responsibilities_en, null, 2) : (job.responsibilities_en || '[]'),
      is_active: job.is_active !== false
    });
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingId(null);
    setFormData({
      slug: '',
      title_tr: '',
      title_en: '',
      department: '',
      location: '',
      employment_type: 'full-time',
      description_tr: '',
      description_en: '',
      requirements_tr: '[]',
      requirements_en: '[]',
      responsibilities_tr: '[]',
      responsibilities_en: '[]',
      is_active: true
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsCreating(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let requirements_tr, requirements_en, responsibilities_tr, responsibilities_en;
      try {
        requirements_tr = JSON.parse(formData.requirements_tr);
        requirements_en = JSON.parse(formData.requirements_en);
        responsibilities_tr = JSON.parse(formData.responsibilities_tr);
        responsibilities_en = JSON.parse(formData.responsibilities_en);
      } catch (e) {
        alert('Invalid JSON in requirements/responsibilities field');
        setSaving(false);
        return;
      }

      const saveData = {
        ...formData,
        requirements_tr,
        requirements_en,
        responsibilities_tr,
        responsibilities_en,
        updated_at: new Date().toISOString()
      };

      if (isCreating) {
        const { error } = await supabase.from('career_jobs').insert(saveData);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('career_jobs').update(saveData).eq('id', editingId);
        if (error) throw error;
      }

      await loadJobs();
      handleCancel();
    } catch (error) {
      console.error('Error saving:', error);
      alert('Error saving: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm(language === 'tr' ? 'Bu ilanı silmek istediğinizden emin misiniz?' : 'Are you sure you want to delete this job?')) {
      return;
    }
    try {
      const { error } = await supabase.from('career_jobs').delete().eq('id', id);
      if (error) throw error;
      await loadJobs();
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Error deleting: ' + error.message);
    }
  };

  const toggleActive = async (id, currentState) => {
    try {
      const { error } = await supabase.from('career_jobs').update({ is_active: !currentState }).eq('id', id);
      if (error) throw error;
      await loadJobs();
    } catch (error) {
      console.error('Error toggling:', error);
    }
  };

  const updateApplicationStatus = async (id, status) => {
    try {
      const { error } = await supabase.from('career_applications').update({ status }).eq('id', id);
      if (error) throw error;
      await loadApplications();
    } catch (error) {
      console.error('Error updating application:', error);
    }
  };

  const texts = {
    tr: {
      title: 'Kariyer Yönetimi',
      jobs: 'İş İlanları',
      applications: 'Başvurular',
      addNew: 'Yeni İlan',
      edit: 'Düzenle',
      delete: 'Sil',
      save: 'Kaydet',
      cancel: 'İptal',
      slug: 'URL Slug',
      titleTr: 'Başlık (TR)',
      titleEn: 'Başlık (EN)',
      department: 'Departman',
      location: 'Lokasyon',
      employmentType: 'Çalışma Tipi',
      descTr: 'Açıklama (TR)',
      descEn: 'Açıklama (EN)',
      reqTr: 'Gereksinimler (TR) - JSON',
      reqEn: 'Gereksinimler (EN) - JSON',
      respTr: 'Sorumluluklar (TR) - JSON',
      respEn: 'Sorumluluklar (EN) - JSON',
      active: 'Aktif',
      noJobs: 'Henüz ilan yok',
      noApplications: 'Henüz başvuru yok',
      applicant: 'Aday',
      status: 'Durum',
      date: 'Tarih',
      viewResume: 'CV Görüntüle',
      statusOptions: {
        new: 'Yeni',
        reviewing: 'İnceleniyor',
        interviewed: 'Görüşüldü',
        rejected: 'Reddedildi',
        hired: 'İşe Alındı'
      }
    },
    en: {
      title: 'Career Management',
      jobs: 'Job Listings',
      applications: 'Applications',
      addNew: 'New Job',
      edit: 'Edit',
      delete: 'Delete',
      save: 'Save',
      cancel: 'Cancel',
      slug: 'URL Slug',
      titleTr: 'Title (TR)',
      titleEn: 'Title (EN)',
      department: 'Department',
      location: 'Location',
      employmentType: 'Employment Type',
      descTr: 'Description (TR)',
      descEn: 'Description (EN)',
      reqTr: 'Requirements (TR) - JSON',
      reqEn: 'Requirements (EN) - JSON',
      respTr: 'Responsibilities (TR) - JSON',
      respEn: 'Responsibilities (EN) - JSON',
      active: 'Active',
      noJobs: 'No jobs yet',
      noApplications: 'No applications yet',
      applicant: 'Applicant',
      status: 'Status',
      date: 'Date',
      viewResume: 'View Resume',
      statusOptions: {
        new: 'New',
        reviewing: 'Reviewing',
        interviewed: 'Interviewed',
        rejected: 'Rejected',
        hired: 'Hired'
      }
    }
  };

  const t = texts[language] || texts['tr'];

  if (authLoading || (user && userRole === null)) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  if (userRole && userRole !== 'admin' && userRole !== 'editor') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {language === 'tr' ? 'Erişim Reddedildi' : 'Access Denied'}
          </h1>
          <button onClick={() => navigate('/')} className="mt-4 px-6 py-3 bg-blue-600 text-white rounded-xl">
            {language === 'tr' ? 'Ana Sayfaya Dön' : 'Back to Home'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Breadcrumb */}
          <nav className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-4">
            <Link to="/" className="hover:text-purple-600 dark:hover:text-purple-400">
              {language === 'tr' ? 'Ana Sayfa' : 'Home'}
            </Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/admin" className="hover:text-purple-600 dark:hover:text-purple-400">
              Admin
            </Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-gray-900 dark:text-white font-medium">{t.title}</span>
          </nav>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Briefcase className="w-6 h-6 text-purple-600" />
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t.title}</h1>
            </div>
            {activeTab === 'jobs' && (
              <button
                onClick={handleCreate}
                disabled={isCreating || editingId}
                className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t.addNew}
              </button>
            )}
          </div>
          {/* Tabs */}
          <div className="flex gap-4 mt-4">
            <button
              onClick={() => setActiveTab('jobs')}
              className={`px-4 py-2 rounded-lg font-medium ${
                activeTab === 'jobs' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              <Briefcase className="w-4 h-4 inline mr-2" />
              {t.jobs} ({jobs.length})
            </button>
            <button
              onClick={() => setActiveTab('applications')}
              className={`px-4 py-2 rounded-lg font-medium ${
                activeTab === 'applications' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              <Users className="w-4 h-4 inline mr-2" />
              {t.applications} ({applications.length})
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'jobs' && (
          <>
            {/* Create/Edit Form */}
            {(isCreating || editingId) && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 mb-8"
              >
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-6">
                  {isCreating ? t.addNew : t.edit}
                </h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.slug}</label>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
                      placeholder="frontend-developer"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.employmentType}</label>
                    <select
                      value={formData.employment_type}
                      onChange={(e) => setFormData({ ...formData, employment_type: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
                    >
                      {employmentTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {language === 'tr' ? type.labelTr : type.labelEn}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.titleTr}</label>
                    <input
                      type="text"
                      value={formData.title_tr}
                      onChange={(e) => setFormData({ ...formData, title_tr: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.titleEn}</label>
                    <input
                      type="text"
                      value={formData.title_en}
                      onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.department}</label>
                    <input
                      type="text"
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
                      placeholder="Engineering"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.location}</label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
                      placeholder="Istanbul / Remote"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.descTr}</label>
                    <textarea
                      rows={3}
                      value={formData.description_tr}
                      onChange={(e) => setFormData({ ...formData, description_tr: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg resize-none"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.descEn}</label>
                    <textarea
                      rows={3}
                      value={formData.description_en}
                      onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.reqTr}</label>
                    <textarea
                      rows={4}
                      value={formData.requirements_tr}
                      onChange={(e) => setFormData({ ...formData, requirements_tr: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg font-mono text-sm resize-none"
                      placeholder='["Requirement 1", "Requirement 2"]'
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.reqEn}</label>
                    <textarea
                      rows={4}
                      value={formData.requirements_en}
                      onChange={(e) => setFormData({ ...formData, requirements_en: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg font-mono text-sm resize-none"
                      placeholder='["Requirement 1", "Requirement 2"]'
                    />
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        className="w-4 h-4 rounded"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t.active}</span>
                    </label>
                  </div>
                </div>

                <div className="flex items-center gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button onClick={handleSave} disabled={saving} className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    {t.save}
                  </button>
                  <button onClick={handleCancel} className="inline-flex items-center px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg">
                    <X className="w-4 h-4 mr-2" />{t.cancel}
                  </button>
                </div>
              </motion.div>
            )}

            {/* Jobs List */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
              </div>
            ) : jobs.length === 0 ? (
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-12 text-center">
                <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">{t.noJobs}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {jobs.map((job, idx) => (
                  <motion.div
                    key={job.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className={`bg-white dark:bg-gray-900 border rounded-xl p-4 ${
                      job.is_active ? 'border-gray-200 dark:border-gray-800' : 'border-red-200 dark:border-red-900 opacity-60'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-gray-900 dark:text-white">{job.title_tr}</span>
                          <span className="text-gray-400">|</span>
                          <span className="text-gray-600 dark:text-gray-400">{job.title_en}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span>{job.department}</span>
                          <span>•</span>
                          <span>{job.location}</span>
                          <span>•</span>
                          <span>{employmentTypes.find(et => et.value === job.employment_type)?.[language === 'tr' ? 'labelTr' : 'labelEn']}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => toggleActive(job.id, job.is_active)} className={`p-2 rounded-lg ${job.is_active ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-50'}`}>
                          {job.is_active ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                        </button>
                        <button onClick={() => handleEdit(job)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg">
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button onClick={() => handleDelete(job.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg">
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'applications' && (
          <>
            {applications.length === 0 ? (
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-12 text-center">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">{t.noApplications}</p>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">{t.applicant}</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">{language === 'tr' ? 'Pozisyon' : 'Position'}</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">{t.status}</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">{t.date}</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">CV</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {applications.map((app) => (
                      <tr key={app.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">{app.full_name}</p>
                            <p className="text-sm text-gray-500">{app.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                          {app.career_jobs?.[language === 'tr' ? 'title_tr' : 'title_en'] || '-'}
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={app.status || 'new'}
                            onChange={(e) => updateApplicationStatus(app.id, e.target.value)}
                            className="px-2 py-1 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded"
                          >
                            {Object.entries(t.statusOptions).map(([value, label]) => (
                              <option key={value} value={value}>{label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {new Date(app.created_at).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US')}
                        </td>
                        <td className="px-4 py-3">
                          {app.resume_url && (
                            <a href={app.resume_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                              {t.viewResume}
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminCareerJobs;
